import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';

import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { GeneralInfoSection } from 'pages/Categories/CategoryDetails/GeneralInfoSection';

import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';

import { useCreateCategoryMutation } from 'data/__generated__';

type FormData = {
    image: File | null;
    title: string;
    indexDisplay: number;
    isPunctual: boolean;
    isFreefood: boolean;
    isMicrostore: boolean;
    shouldShowSubcategories: boolean;
};

export type InitialCategoryType = Omit<FormData, 'image'> & {
    imageUrl: string;
};

const defaultCategory = {
    title: '',
    imageUrl: '',
    indexDisplay: 0,
    isFreefood: false,
    isMicrostore: false,
    isPunctual: false,
    shouldShowSubcategories: false,
};

export const CreateCategory = () => {
    const navigate = useNavigate();

    const [createCategory, { loading: creationLoading }] = useCreateCategoryMutation();

    const methods = useForm<FormData>();

    const {
        formState: { isDirty },
        handleSubmit,
    } = methods;

    const initialCategory: InitialCategoryType = { ...defaultCategory };

    const handleFormSubmit = handleSubmit(async (fields) => {
        const { data } = await createCategory({
            variables: {
                fields,
            },
        });

        if (data) {
            const {
                createCategory: { success, error, categoryId },
            } = data;
            if (success) {
                toast.success(`La catégorie "${fields.title}" a bien été créée !`);
                navigate(`/category/${categoryId}`);
            } else {
                if (error) {
                    toast.error('error');
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    });

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/categories' }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={handleFormSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.categoryDetails} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={handleFormSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={creationLoading}
                            saveTitle="Créer"
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection category={initialCategory} />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
